import { useCreateStockDocumentMutation } from 'store/slices/apiV1/inventory';
import { StockDocType } from 'types/inventory';
import { Org } from 'types/org';
import { snakeCase } from 'utils/functions';
import { FlagshipModelOptions } from 'constants/recordTypes';

interface StockDocData {
  document?: string;
  file?: File;
  documentType: StockDocType;
  filename: string;
  processDocument?: boolean;
  notes?: string;
  receiveAi?: boolean;
  asyncReceiveAi?: boolean;
  autoClassify?: boolean;
  overrideOrgId?: Org['id'];
  metadata?: object;
  createdFromObjectId?: string;
  createdFromObjectType?: FlagshipModelOptions;
}

export function useCreateStockDocsV2() {
  const [createStockDocument, { isLoading: isLoadingCreateStockDoc }] =
    useCreateStockDocumentMutation();

  const createStockDoc = async (d: StockDocData) => {
    const stockDocData = new FormData();
    stockDocData.append('document_type', d.documentType);
    stockDocData.append('shared_document', 'false');
    stockDocData.append('v2_mode', 'true');
    stockDocData.append('name', d.filename);
    if (d.createdFromObjectId)
      stockDocData.append('created_from_object_id', d.createdFromObjectId);
    if (d.createdFromObjectType)
      stockDocData.append('created_from_object_type', d.createdFromObjectType);
    if (d.metadata) {
      stockDocData.append('metadata', JSON.stringify(snakeCase(d.metadata)));
    }

    if (d.notes) stockDocData.append('notes', d.notes);
    if (d.processDocument) stockDocData.append('process_document', 'true');
    else stockDocData.append('process_document', 'false');

    if (d.receiveAi) stockDocData.append('receive_ai', 'true');
    if (d.asyncReceiveAi) stockDocData.append('async_receive_ai', 'true');
    if (d.autoClassify) stockDocData.append('auto_classify', 'true');
    if (d.document) {
      await fetch(d.document)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], d.filename);
          stockDocData.append('document', file);
        });
    }

    if (d.file) stockDocData.append('document', d.file);
    const result = await createStockDocument({
      payload: stockDocData,
      overrideOrgId: d.overrideOrgId,
    }).unwrap();

    return result;
  };

  const createStockDocuments = async (data: StockDocData[]) => {
    const results = await Promise.allSettled(
      data.map((d) => createStockDoc(d))
    );
    return results;
  };
  return {
    isLoading: isLoadingCreateStockDoc,
    createStockDocuments,
  };
}
